/*
 * Lista de filtros para la lista de traspasos.
 *
 * Contiene los componentes solicitados para filtrar la lista de los registros de
 * traspasos.
 *
 * Created on Mon Mar 27 2023 by José Luis Chávez Ayala.
 *
 * Copyright (c) 2023 Turn My App.
 */

<template>
  <div>

    <!-- #region begin:title -->
    <b-form-row>
      <b-col
        cols="12"
        class="mb-1"
      >
        <h3>Traspasos</h3>
      </b-col>
    </b-form-row>
    <!-- #endregion end:title -->

    <!-- #region begin:: List options -->
    <b-form-row>
      <b-col :md="canManageTransfers ? '5' : '6'">
        <b-form-row>

          <!-- #region begin::Searcher -->
          <b-col md="4">
            <BasicSearcher
              ref="basic-searcher"
              :callback="handleSearch"
              placeholder="Buscar traspaso"
            />
          </b-col>
          <!-- #endregion end::Searcher -->

          <!-- #region::Status select filter -->
          <b-col md="4">
            <v-select
              v-model="selectedTransferStatus"
              label="text"
              class="style-chooser"
              placeholder="Estatus"
              :options="getTransferStatusList"
            />
          </b-col>
          <!-- #endregion::Status select filter -->

          <!-- #region::Origin location select filter -->
          <b-col md="4">
            <v-select
              v-model="selectedTransferOrigin"
              label="text"
              class="style-chooser"
              placeholder="Origen"
              :options="getLocations"
            />
          </b-col>
          <!-- #endregion::Origin location select filter -->

        </b-form-row>
      </b-col>

      <b-col :md="canManageTransfers ? '5' : '6'">
        <b-form-row>

          <!-- #region::Destination location select filter -->
          <b-col md="4">
            <v-select
              v-model="selectedTransferDestination"
              label="text"
              class="style-chooser"
              placeholder="Destino"
              :options="getLocations"
            />
          </b-col>
          <!-- #endregion::Destination location select filter -->

          <!-- #region::ID sorter filter -->
          <b-col md="4">
            <v-select
              v-model="selectedIdOption"
              label="text"
              class="style-chooser"
              placeholder="ID"
              :options="getIdOptions"
            >
              <template #option="{ text, icon }">
                <feather-icon
                  :icon="icon"
                  size="16"
                  class="align-middle mr-50"
                />
                <span> {{ text }}</span>
              </template>
            </v-select>
          </b-col>
          <!-- #endregion::ID sorter filter -->

          <!-- #region::Required date filter -->
          <b-col
            lg="4"
          >
            <b-form-datepicker
              id="transfer-datepicker"
              v-model="selectedTransferDate"
              :date-format-options="{ year: 'numeric', month: 'short', day: '2-digit', weekday: 'short' }"
              placeholder="Fecha de creación"
              local="es"
              reset-button
            />
          </b-col>
          <!-- #endregion::Required date filter -->

        </b-form-row>
      </b-col>

      <!-- #region begin::New cicuit button -->
      <b-col
        v-if="$ability.can('create', 'Transfer') && canManageTransfers"
        cols="12"
        md="auto"
        class="ml-auto"
      ><b-button
        variant="principal-btn"
        class="my-1 my-lg-0 w-100 principal-btn"
        @click="$router.push({ name: 'new-transfer' })"
      >
        <feather-icon
          icon="PlusIcon"
          class="mr-50"
        />
        <span class="align-middle">Nuevo traspaso</span>
      </b-button></b-col>
      <!-- #endregion end::New cicuit button -->

    </b-form-row>
    <!-- #endregion begin:: List options -->

  </div>
</template>

<script>
// #region Imports
import { mapGetters } from 'vuex'
import {
  BButton, BCol, BFormRow, BFormDatepicker,
} from 'bootstrap-vue'

import vSelect from 'vue-select'
import BasicSearcher from '@/components/forms/BasicSearcher.vue'
// #endregion

export default {
  components: {
    BCol,
    BButton,
    vSelect,
    BFormRow,
    BasicSearcher,
    BFormDatepicker,
  },
  props: {
    /**
     * Determina si se cargan los filtros globales del estado
     */
    canApplyGlobalFilters: {
      type: Boolean,
      default: true,
    },
    canManageTransfers: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      selectedIdOption: '',
      selectedTransferDate: '',
      selectedTransferStatus: '',
      selectedTransferOrigin: '',
      selectedTransferDestination: '',
    }
  },
  computed: {
    ...mapGetters({
      getIdOptions: 'quotes/getIdOptions',
      getLocations: 'transfers/getLocations',
      getTransferStatusList: 'transfers/getTransferStatusList',

      /**
       * Filtros globales
       */
      getFilteredSortBy: 'filters/getFilteredSortBy',
      getFilteredOrigin: 'filters/getFilteredOrigin',
      getFilteredStatus: 'filters/getFilteredStatus',
      getFilteredSearchTerm: 'filters/getFilteredSearchTerm',
      getFilteredDestination: 'filters/getFilteredDestination',
      getFilteredCreatedAtDate: 'filters/getFilteredCreatedAtDate',
    }),
    /**
     * Ordenamiento filtrado (filtro en store)
     */
    filteredSortBy: {
      get() { return this.getFilteredSortBy },
    },
    /**
     * Origen filtrado (filtro en store)
     */
    filteredOrigin: {
      get() { return this.getFilteredOrigin },
    },
    /**
     * Estatus filtrado (filtro en store)
     */
    filteredStatus: {
      get() { return this.getFilteredStatus },
    },
    /**
     * Término de búsqueda filtrado (filtro en store)
     */
    filteredSearchTerm: {
      get() { return this.getFilteredSearchTerm },
    },
    /**
     * Destino filtrado (filtro en store)
     */
    filteredDestination: {
      get() { return this.getFilteredDestination },
    },
    /**
     * Fecha de creación filtrada (filtro en store)
     */
    filteredCreatedAtDate: {
      get() { return this.getFilteredCreatedAtDate },
    },
  },

  /**
  * Hook que se ejecuta cuando el componente se ha montado
  *
  * @summary Si existe información relacionada con los filtros cargados en el store,
  * se cargan en el componente. Se establece un observador para cada filtro
  */
  mounted() {
    // If we can apply global filters, load them from the store into the filter component
    if (this.canApplyGlobalFilters) {
      // Carga el término de búsqueda (filtro en store)
      if (this.filteredSearchTerm) {
        this.$refs['basic-searcher'].loadSearchTerm(this.filteredSearchTerm)
      }

      // Carga la fecha de creación (filtro en store)
      this.selectedTransferDate = this.filteredCreatedAtDate || ''

      // Carga el ordenamiento (filtro en store)
      this.selectedIdOption = this.filteredSortBy ? { text: this.filteredSortBy } : ''

      // Carga el estatus (filtro en store)
      this.selectedTransferStatus = this.filteredStatus ? { text: this.filteredStatus } : ''

      // Carga el origen (filtro en store)
      this.selectedTransferOrigin = this.filteredOrigin ? { text: this.filteredOrigin } : ''

      // Carga el destino (filtro en store)
      this.selectedTransferDestination = this.filteredDestination ? { text: this.filteredDestination } : ''
    }

    this.$watch('selectedTransferDate', date => {
      this.$emit('on:filter', { type: 'date', value: date })
    })

    this.$watch('selectedIdOption', idOption => {
      this.$emit('on:filter', { type: 'sort', value: idOption !== null ? idOption.text : '' })
    })

    this.$watch('selectedTransferStatus', status => {
      this.$emit('on:filter', { type: 'status', value: status !== null ? status.text : '' })
    })

    this.$watch('selectedTransferOrigin', origin => {
      this.$emit('on:filter', { type: 'origin', value: origin !== null ? origin.text : '' })
    })

    this.$watch('selectedTransferDestination', destination => {
      this.$emit('on:filter', { type: 'destination', value: destination !== null ? destination.text : '' })
    })
  },
  methods: {
    handleSearch(searchTerm) {
      this.$emit('on:filter', { type: 'search', value: searchTerm })

      this.selectedIdOption = ''
      this.selectedTransferDate = ''
      this.selectedTransferStatus = ''
      this.selectedTransferOrigin = ''
      this.selectedTransferDestination = ''
    },
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import '@core/scss/vue/libs/vue-autosuggest.scss';
.style-chooser {
  background: #ffffff;
}
</style>
